import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { logout, loginUser, updateUserSettings } from '../redux/authSlice';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, status, error } = useSelector((state) => state.auth, (left, right) => {
    // Custom equality function to prevent unnecessary re-renders
    return left.user?.id === right.user?.id &&
           left.status === right.status &&
           left.error === right.error;
  });

  // Log state changes in development only
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Only log when there are meaningful changes (not on every render)
      console.log('AuthContext state changed:', {
        hasUser: !!user,
        user: user ? { id: user.id, username: user.username } : null, // Only log essential user info
        status,
        error
      });
    }
  }, [user, status, error]);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const handleLogin = useCallback(async (credentials) => {
    try {
      const result = await dispatch(loginUser(credentials)).unwrap();
      return result;
    } catch (err) {
      console.error('Login failed:', err);
      throw err;
    }
  }, [dispatch]);

  const handleUpdateUser = useCallback(async (updates) => {
    try {
      const result = await dispatch(updateUserSettings(updates)).unwrap();
      return result;
    } catch (err) {
      console.error('User update failed:', err);
      throw err;
    }
  }, [dispatch]);

  const value = useMemo(() => ({
    user,
    status,
    error,
    logout: handleLogout,
    login: handleLogin,
    updateUser: handleUpdateUser,
    isAuthenticated: !!user
  }), [user, status, error, handleLogout, handleLogin, handleUpdateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
